import React, { BaseSyntheticEvent } from 'react';
import { UnpackNestedValue } from 'react-hook-form';
import { Grid, MenuItem } from '@material-ui/core';
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledSelect,
  GridFormElement,
} from '../form-controls';
import { MuiPickersUtilsProvider } from '@dls/react-core';
import DateFnsUtils from '@date-io/date-fns';
import { FormFooter } from '../styled/form-footer';
import { Subject } from '../../api/studykit';
import { format } from 'date-fns';
import {
  AsyncSubmitHandler,
  useFormWithServerErrorHandling,
} from '../../helpers/form-error-handling';

type SubjectFormValues = Omit<Subject, 'dateOfBirth'> & { dateOfBirth?: Date };

interface SubjectFormProps {
  onSubmit: AsyncSubmitHandler<Subject>;
  subject?: Subject;
  onCancel?: () => void;
}

export const SubjectForm: React.FC<SubjectFormProps> = ({ onSubmit, subject, onCancel }) => {
  const {
    reset,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useFormWithServerErrorHandling<SubjectFormValues>({
    defaultValues: subject && {
      ...subject,
      handedness: subject.handedness || 'unknown',
      gender: subject.gender || 'unknown',
      dateOfBirth: subject.dateOfBirth ? new Date(subject.dateOfBirth) : undefined,
    },
  });

  const parseFieldsBeforeSubmitting = (
    data: UnpackNestedValue<SubjectFormValues>,
    event?: BaseSyntheticEvent,
  ) => {
    const subject = {
      ...data,
      dateOfBirth: data.dateOfBirth ? format(data.dateOfBirth, 'yyyy-MM-dd') : undefined,
      startDate: new Date(data.startDate!).toISOString(),
      endDate: new Date(data.endDate!).toISOString(),
      // react-form-hook lib does not support valueAsNumber for controlled MUI components
      // https://github.com/react-hook-form/react-hook-form/discussions/8068
      weightKg: data.weightKg ? parseFloat(data.weightKg.toString()) : undefined,
      heightCm: data.heightCm ? parseFloat(data.heightCm.toString()) : undefined,
      handedness: data.handedness === 'unknown' ? undefined : data.handedness,
      gender: data.gender === 'unknown' ? undefined : data.gender,
    } as Subject;
    return onSubmit(subject, event);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <form onSubmit={handleSubmit(parseFieldsBeforeSubmitting)}>
        <Grid container spacing={3}>
          <GridFormElement label={'Name'}>
            <ControlledInput
              control={control}
              name="name"
              rules={{ required: true }}
              inputProps={{ fullWidth: true, placeholder: '' }}
            />
          </GridFormElement>
          <GridFormElement label={'Gender'}>
            <ControlledSelect
              selectProps={{ fullWidth: true }}
              rules={{ required: false }}
              control={control}
              name={'gender'}
              defaultValue={'unknown'}
            >
              <MenuItem value={'unknown'}>
                <span>Unknown</span>
              </MenuItem>
              <MenuItem value={'male'}>
                <span>Male</span>
              </MenuItem>
              <MenuItem value={'female'}>
                <span>Female</span>
              </MenuItem>
              <MenuItem value={'other'}>
                <span>Other</span>
              </MenuItem>
            </ControlledSelect>
          </GridFormElement>
          <GridFormElement label="Weight (kg)">
            <ControlledInput
              control={control}
              name="weightKg"
              rules={{ required: false }}
              inputProps={{ fullWidth: true, type: 'number' }}
            />
          </GridFormElement>
          <GridFormElement label={'Date of Birth'}>
            <ControlledDatePicker
              control={control}
              name={'dateOfBirth'}
              rules={{ required: false }}
              datePickerProps={{ fullWidth: true }}
            />
          </GridFormElement>
          <GridFormElement label="Height (cm)">
            <ControlledInput
              control={control}
              name="heightCm"
              rules={{ required: false }}
              inputProps={{ fullWidth: true, type: 'number' }}
            />
          </GridFormElement>
          <GridFormElement label={'Handedness'}>
            <ControlledSelect
              selectProps={{ fullWidth: true }}
              rules={{ required: false }}
              control={control}
              name={'handedness'}
              defaultValue={'unknown'}
            >
              <MenuItem value={'unknown'}>
                <span>Unknown</span>
              </MenuItem>
              <MenuItem value={'right'}>
                <span>Right</span>
              </MenuItem>
              <MenuItem value={'left'}>
                <span>Left</span>
              </MenuItem>
            </ControlledSelect>
          </GridFormElement>
          <GridFormElement label={'Start Date'}>
            <ControlledDatePicker
              control={control}
              name={'startDate'}
              rules={{ required: true }}
              datePickerProps={{ fullWidth: true }}
            />
          </GridFormElement>
          <GridFormElement label={'End Date'}>
            <ControlledDatePicker
              control={control}
              name={'endDate'}
              rules={{ required: true }}
              datePickerProps={{ fullWidth: true }}
            />
          </GridFormElement>
        </Grid>
        <FormFooter
          isSubmitting={isSubmitting}
          onCancel={onCancel ? onCancel : () => reset()}
          cancelButtonText={onCancel ? 'Cancel' : 'Reset'}
        />
      </form>
    </MuiPickersUtilsProvider>
  );
};
